import { Box } from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import withAuth from "../../HOC/withAuth";

import pharmaImage from "../../Assets/images/phamagiant.png";
import databassImage from "../../Assets/images/databass.png";
import authorcafeImage from "../../Assets/images/authorcafe.png";
import neumaImage from "../../Assets/images/neuma.png";
import project5Image from "../../Assets/images/coding.png";
import project6Image from "../../Assets/images/emotionchat.png";
import owlyImage from "../../Assets/images/owly.png";

const Home = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Add parallax hover effect to project cards
    const addParallaxEffect = (
      card: HTMLElement,
      isHeroCard: boolean = false
    ) => {
      let bounds: DOMRect;

      const rotateToMouse = (e: MouseEvent) => {
        bounds = card.getBoundingClientRect();
        const mouseX = e.clientX;
        const mouseY = e.clientY;
        const leftX = mouseX - bounds.x;
        const topY = mouseY - bounds.y;
        const center = {
          x: leftX - bounds.width / 2,
          y: topY - bounds.height / 2,
        };

        const rotationDivisorX = isHeroCard ? 160 : 20;
        const rotationDivisorY = isHeroCard ? 80 : 20;
        const scale = isHeroCard ? 1.00156 : 1.02;
        const shadowDivisorX = isHeroCard ? 160 : 20;
        const shadowDivisorY = isHeroCard ? 80 : 20;
        const shadowOffset = isHeroCard ? 0.624 : 10;
        const shadowOpacity = isHeroCard ? 0.00936 : 0.08;

        card.style.transform = `
          perspective(500px)
          rotateX(${center.y / rotationDivisorY}deg)
          rotateY(${-center.x / rotationDivisorX}deg)
          scale3d(${scale}, ${scale}, ${scale})
        `;
        card.style.boxShadow = `
          ${center.x / shadowDivisorX}px ${
          center.y / shadowDivisorY + shadowOffset
        }px 30px rgba(0, 0, 0, ${shadowOpacity})
        `;
      };

      const handleMouseEnter = () => {
        bounds = card.getBoundingClientRect();
        document.addEventListener("mousemove", rotateToMouse);
      };

      const handleMouseLeave = () => {
        document.removeEventListener("mousemove", rotateToMouse);
        card.style.transform = "";
        card.style.boxShadow = isHeroCard
          ? "12px 12px 0 #0021F5"
          : "8px 8px 0 #000";
      };

      card.addEventListener("mouseenter", handleMouseEnter);
      card.addEventListener("mouseleave", handleMouseLeave);

      // per-card cleanup
      return () => {
        card.removeEventListener("mouseenter", handleMouseEnter);
        card.removeEventListener("mouseleave", handleMouseLeave);
        document.removeEventListener("mousemove", rotateToMouse);
      };
    };

    // Apply to hero project card with special flag
    const heroCard = document.querySelector(
      ".hero-project-card"
    ) as HTMLElement | null;
    if (heroCard) heroCard.style.boxShadow = "12px 12px 0 #0021F5";
    const heroCleanup = heroCard ? addParallaxEffect(heroCard, true) : null;

    // Apply to all project cards
    const projectCards = document.querySelectorAll(".project-card");
    const cleanups = Array.from(projectCards).map((card) => {
      (card as HTMLElement).style.boxShadow = "8px 8px 0 #000";
      return addParallaxEffect(card as HTMLElement, false);
    });

    // --- Polished custom cursor over "Aditya" ---
    // --- Polished custom cursor with animated sparkline ---
    const addBuildCursor = () => {
      const nameEl = document.querySelector(
        ".philosophy-highlight"
      ) as HTMLElement | null;
      if (!nameEl) return;

      // Skip on touch devices
      const isTouch = matchMedia("(pointer: coarse)").matches;
      if (isTouch) return;

      let cursorEl: HTMLDivElement | null = null;
      let raf = 0; // follower rAF
      let sparkRAF = 0; // sparkline rAF
      let sparkTimer: number | null = null;
      let targetX = 0,
        targetY = 0;
      let curX = 0,
        curY = 0;

      // Create cursor node with inline SVG sparkline
      const makeCursor = () => {
        const el = document.createElement("div");
        el.className = "build-cursor";
        el.setAttribute("aria-hidden", "true");
        el.innerHTML = `
    <div class="build-cursor__badge">
      <svg class="sparkline" viewBox="-3 -3 66 26" width="60" height="20" preserveAspectRatio="none">
        <g class="sparkline__g" /* draw inside this group */>
          <path class="sparkline__path" d="" />
          <circle class="sparkline__glow" r="3" cx="0" cy="0" />
        </g>
      </svg>
      <span>Less chaos, more clarity</span>
    </div>
  `;

        document.body.appendChild(el);
        return el;
      };

      // Generate a clean sparkline path "d" (M + L commands)
      // Generate a sparkline path that trends upward
      // Draw a growing sparkline inside an inset area to avoid clipping
      // Upward sparkline with extra end lift (finishes noticeably higher)
      const makeSparkPath = (W = 60, H = 20, points = 14) => {
        const M = 3; // margin (match your viewBox padding)
        const w = W - M * 2;
        const h = H - M * 2;

        const step = w / (points - 1);
        const arr: { x: number; y: number }[] = [];

        const baseY = M + h - 1; // start near bottom
        const baseRise = (h - 6) / (points - 1); // stronger climb across width
        const endLiftPx = h * 0.22; // extra push near the end (22% of height)

        // cubic ease-out for the additional end lift
        const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

        for (let i = 0; i < points; i++) {
          const x = M + i * step;
          const t = i / (points - 1); // 0 → left, 1 → right

          // upward baseline
          let y = baseY - i * baseRise;

          // extra end lift (stronger near the right end)
          y -= easeOut(Math.max(0, (t - 0.35) / 0.65)) * endLiftPx;

          // small wiggle; taper as we approach the end so it doesn't clip
          const noiseAmp = 5 * (1 - t * 0.7); // ~±2.5px at left → ~±1.5px at right
          const noise = (Math.random() - 0.5) * noiseAmp;
          y += noise;

          // clamp to inset area
          y = Math.min(M + h - 1, Math.max(M + 1, y));
          arr.push({ x, y });
        }

        return (
          "M " +
          arr
            .map((p, i) => `${i ? "L" : ""}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
            .join(" ")
        );
      };

      // Animate drawing the path + move glow dot along it
      const playSparkline = (svg: SVGSVGElement) => {
        const path = svg.querySelector(".sparkline__path") as SVGPathElement;
        const dot = svg.querySelector(".sparkline__glow") as SVGCircleElement;
        if (!path || !dot) return;

        // fresh upward path
        const d = makeSparkPath(60, 20, 14);
        path.setAttribute("d", d);

        // dash drawing
        const length = path.getTotalLength();
        path.style.strokeDasharray = `${length}`;
        path.style.strokeDashoffset = `${length}`;

        const duration = 900;
        const start = performance.now();

        const animate = (t: number) => {
          const k = Math.min(1, (t - start) / duration);
          const dash = length * (1 - k);
          path.style.strokeDashoffset = `${dash}`;

          // move glow along currently revealed length
          const pt = path.getPointAtLength(length * k);
          dot.setAttribute("cx", `${pt.x}`);
          dot.setAttribute("cy", `${pt.y}`);

          if (k < 1) requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
      };

      const onEnter = () => {
        if (!cursorEl) cursorEl = makeCursor();

        // Start near the pointer for a buttery feel
        curX = targetX;
        curY = targetY;
        cursorEl.style.opacity = "1";
        cursorEl.style.transform = "translate(-50%, -50%) scale(1)";

        // Play the sparkline build, and refresh it every ~2s while hovering
        const svg = cursorEl.querySelector("svg.sparkline") as SVGSVGElement;
        playSparkline(svg);
        if (sparkTimer) clearInterval(sparkTimer);
        sparkTimer = window.setInterval(
          () => playSparkline(svg),
          2000
        ) as unknown as number;

        loop();
      };

      const onLeave = () => {
        cancelAnimationFrame(raf);
        cancelAnimationFrame(sparkRAF);
        if (sparkTimer) {
          clearInterval(sparkTimer);
          sparkTimer = null;
        }
        if (cursorEl) {
          cursorEl.style.opacity = "0";
          cursorEl.style.transform = "translate(-50%, -50%) scale(.96)";
        }
      };

      const onMove = (e: MouseEvent) => {
        targetX = e.clientX + 16; // tasteful offset from pointer
        targetY = e.clientY + 16;
      };

      // Smooth follower
      const loop = () => {
        curX += (targetX - curX) * 0.18;
        curY += (targetY - curY) * 0.18;
        if (cursorEl) {
          cursorEl.style.left = `${curX}px`;
          cursorEl.style.top = `${curY}px`;
        }
        raf = requestAnimationFrame(loop);
      };

      nameEl.addEventListener("mouseenter", onEnter);
      nameEl.addEventListener("mouseleave", onLeave);
      nameEl.addEventListener("mousemove", onMove);

      // Cleanup
      return () => {
        nameEl.removeEventListener("mouseenter", onEnter);
        nameEl.removeEventListener("mouseleave", onLeave);
        nameEl.removeEventListener("mousemove", onMove);
        cancelAnimationFrame(raf);
        cancelAnimationFrame(sparkRAF);
        if (sparkTimer) clearInterval(sparkTimer);
        if (cursorEl) cursorEl.remove();
      };
    };

    const buildCursorCleanup = addBuildCursor(); // << create it BEFORE return

    // Cleanup on unmount (run all the cleanups)
    return () => {
      if (heroCleanup) heroCleanup();
      cleanups.forEach((c) => c());
      if (buildCursorCleanup) buildCursorCleanup(); // << include here
    };
  }, []);

  // replace your existing handler with this
  const handleProjectClick = (link: string) => {
    const isExternal = /^https?:\/\//i.test(link);
    if (isExternal) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      navigate(link);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", overflow: "hidden" }}>
      <style>
        {`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          /* Add these styles inside your existing <style> block */
          .project-image {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;  /* Changed from cover to contain */
            object-position: center center;
            display: block;
        }
          
          /* Hero Project Image */
          .hero-project-image-container {
              background: #fafafa;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              padding: 20px;  /* Add padding for breathing room */
              height: 100%;
              min-height: 300px;
          }
          
          .hero-project-image-container img {
              max-width: 100%;
              max-height: 100%;
              width: auto;
              height: auto;
              object-fit: contain;  /* Changed from cover to contain */
              object-position: center center;
              margin: 0;
              padding: 0;
              display: block;
          }

          body {
            font-family: 'DM Sans', sans-serif;
            background: #ffffff;
            color: #333;
            overflow-x: hidden;
          }
          
          @media (max-width: 768px) {
            .philosophy-highlight svg {
              height: 15px;
              bottom: -2px;
            }
            
            .philosophy-highlight::after {
              height: 15px;
            }
          }

          /* Hero Section */
          .hero-section {
            min-height: 100vh;
            padding: 80px 60px;
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            position: relative;
          }
          
          /* Intro Section */
          .intro-section {
            margin-bottom: 120px;
          }
          
          
          
          .name-title {
            font-size: clamp(40px, 5vw, 60px);
            font-weight: 600;
            margin-bottom: 20px;
            letter-spacing: -1px;
          }
          
          .name-title strong {
            font-weight: 900;
            color: #0021F5;
          }
          
          .design-philosophy {
            font-size: clamp(24px, 3vw, 36px);
            font-weight: 600;
            line-height: 1.3;
            max-width: 900px;
            margin-bottom: 40px;
            color: #333;
          }
          
          .philosophy-highlight {
            color: #0021F5;
            position: relative;
            display: inline-block;
            cursor: pointer;
            overflow: visible;
            padding-bottom: 8px;
            margin-bottom: -8px;
            vertical-align: baseline;
          }
          
          .philosophy-highlight::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: -2px;
            right: -2px;
            height: 20px;
            width: calc(100% + 4px);
            pointer-events: none;
          }
          
          .philosophy-highlight svg {
            position: absolute;
            bottom: -4px;
            left: 0;
            width: 100%;
            height: 20px;
            overflow: hidden;
          }
          
          /* 1) Base: always moving; draw once on load */
          @property --speed { syntax: "<time>"; inherits: true; initial-value: 1s; }
          
          .philosophy-highlight svg path {
            stroke: #ADFF00;
            stroke-width: 4;
            fill: none;
            stroke-linecap: round;
            stroke-linejoin: round;
          
            /* keep motion continuous via a CSS var (no restart) */
            animation:
              moveZigzag var(--speed) linear infinite,
              drawOnce 1.1s ease-out forwards;   /* runs only on load, then sticks */
          
            /* set up the "draw" */
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
            will-change: transform, stroke-dashoffset;
          }
          
          /* 2) On hover: only change speed (do NOT touch animation or dashoffset) */
          .philosophy-highlight:hover svg path {
            --speed: 0.75s; /* speeds up smoothly without restarting */
          }
          
          /* move loop */
          @keyframes moveZigzag {
            from { transform: translateX(0); }
            to   { transform: translateX(30px); }
          }
          
          /* one-time reveal on page load; leaves dashoffset at 0 (visible) */
          @keyframes drawOnce {
            to { stroke-dashoffset: 0; }
          }

          @keyframes drawZigzag {
            to {
              stroke-dashoffset: 0;
            }
          }
          
          @keyframes underlineReveal {
            to {
              transform: scaleX(1);
            }
          }
          
          .bio-pills {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
          }
          
          .bio-pill {
            padding: 15px 30px;
            background: #f5f5f5;
            border: 4px solid #000;
            box-shadow: 5px 5px 0 #000;
            font-size: 18px;
            font-weight: 900;
            text-transform: uppercase;
            display: flex;
            align-items: center;
            gap: 12px;
            position: relative;
            cursor: default;
          }
          
          .bio-pill::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(0,0,0,0.03) 10px,
              rgba(0,0,0,0.03) 20px
            );
            pointer-events: none;
          }
          
          .bio-pill span {
            font-size: 24px;
          }
          
          /* Impact Section */
          .impact-section {
            margin-bottom: 60px;
          }
          
          .impact-header {
            display: flex;
            align-items: baseline;
            gap: 20px;
            margin-bottom: 40px;
          }
          
          .impact-title {
            font-size: clamp(60px, 8vw, 100px);
            font-weight: 900;
            font-style: italic;
            text-transform: uppercase;
            color: #0021F5;
            letter-spacing: -4px;
            line-height: 0.9;
          }
          
          .impact-subtitle {
            font-size: 18px;
            color: #666;
            font-weight: 500;
          }
          
          /* Impact Metrics Grid */
          .impact-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 30px;
            margin-bottom: 80px;
          }
          
          .impact-card {
            background: #0021F5;
            color: white;
            padding: 40px;
            position: relative;
            overflow: hidden;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          
          .impact-card:nth-child(1) { animation-delay: 1s; }
          .impact-card:nth-child(2) { animation-delay: 1.1s; }
          .impact-card:nth-child(3) { animation-delay: 1.2s; }
          .impact-card:nth-child(4) { animation-delay: 1.3s; }
          
          .impact-card:hover {
            transform: translateY(-5px) scale(1.02);
            box-shadow: 0 20px 40px rgba(0,33,245,0.3);
          }
          
          .impact-card::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(173,255,0,0.3) 0%, transparent 70%);
            animation: pulse 3s ease-in-out infinite;
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.1); opacity: 0.3; }
          }
          
          .impact-number {
            font-size: clamp(48px, 6vw, 72px);
            font-weight: 900;
            margin-bottom: 10px;
            color: #ADFF00;
            text-shadow: 2px 2px 0 rgba(0,0,0,0.1);
          }
          
          .impact-label {
            font-size: 18px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .impact-context {
            font-size: 14px;
            margin-top: 5px;
          }
          
          /* Projects Preview Section */
        
          
          .projects-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 40px;
          }
          
          .projects-title {
            font-size: 24px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .view-all-link {
            color: #0021F5;
            text-decoration: none;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: gap 0.3s ease;
            cursor: pointer;
          }
          
          .view-all-link:hover {
            gap: 12px;
          }
          
          /* Hero Project Card */
          .hero-project-card {
            background: white;
            border: 4px solid #000;
            cursor: pointer;
            transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
            position: relative;
            margin-bottom: 70px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            min-height: 400px;
            transform-style: preserve-3d;
            will-change: transform;
            z-index: 1;
            box-shadow: 12px 12px 0 #0021F5;
            margin-right: 12px;
          }
          
          .hero-project-card:hover {
            transform: translate(-4px, -4px);
            box-shadow: 16px 16px 0 #0021F5;
            z-index: 10;
          }
          
          .hero-project-image {
            background: #f5f5f5;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 120px;
            color: #ddd;
          }
          
          .hero-project-content {
            padding: 60px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          
          .hero-project-tag {
            display: inline-block;
            padding: 8px 20px;
            background: #0021F5;
            color: white;
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 24px;
            width: fit-content;
          }
          
          .hero-project-name {
            font-size: 36px;
            font-weight: 800;
            margin-bottom: 20px;
            text-transform: uppercase;
            letter-spacing: -0.5px;
            line-height: 1.1;
          }
          
          .hero-project-question {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 30px;
            color: #0021F5;
            line-height: 1.3;
          }
          
          .hero-project-impact-label {
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .hero-project-impact-text {
            font-size: 20px;
            color: #333;
            line-height: 1.5;
            margin-bottom: 36px;
          }
          
          .hero-view-case-study {
            color: #0021F5;
            font-weight: 700;
            font-size: 18px;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 10px;
            transition: gap 0.3s ease;
          }
          
          .hero-view-case-study:hover {
            gap: 15px;
          }
          
          /* Regular Project Cards Grid */
          .projects-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 40px;
            width: 100%;
            padding: 10px;
          }
          
          .project-card {
            background: white;
            border: 4px solid #000;
            cursor: pointer;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            will-change: transform;
            box-shadow: 8px 8px 0 #000;
            position: relative;
            margin-bottom: 10px;
            margin-right: 10px;
          }
          
          .project-card:hover {
            transform: translate(-4px, -4px);
            box-shadow: 12px 12px 0 #000;
          }
          
          /* Regular Project Images */
          .project-image-container {
              width: 100%;
              height: 333px;
              background: #fafafa;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              padding: 20px;  /* Add padding for breathing room */
          }
          
          .project-image-placeholder {
            width: 100%;
            height: 250px;
            background: #f5f5f5;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 60px;
            color: #ddd;
          }
          
          .project-content {
            padding: 30px;
          }
          
          .project-name {
            font-size: 18px;
            font-weight: 800;
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #333;
          }
          
          .project-question {
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 20px;
            color: #0021F5;
            line-height: 1.3;
          }
          
          .project-impact-label {
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #666;
            margin-bottom: 8px;
          }
          
          .project-impact-text {
            font-size: 16px;
            color: #333;
            line-height: 1.5;
            margin-bottom: 24px;
          }
          
          .view-case-study {
            color: #0021F5;
            font-weight: 600;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: gap 0.3s ease;
          }
          
          .view-case-study:hover {
            gap: 12px;
          }
          
          /* Responsive */
          @media (max-width: 768px) {
            .hero-section {
              padding: 40px 20px;
              min-height: auto;
            }
            
            .intro-section {
              margin-bottom: 60px;
            }
            
            .name-title {
              font-size: clamp(32px, 8vw, 48px);
            }
            
            .design-philosophy {
              font-size: clamp(20px, 5vw, 28px);
            }
            
            .bio-pills {
              flex-direction: column;
              gap: 15px;
            }
            
            .bio-pill {
              padding: 12px 20px;
              font-size: 16px;
              justify-content: center;
            }
            
            .impact-header {
              flex-direction: column;
              gap: 10px;
              align-items: flex-start;
            }
            
            .impact-title {
              font-size: clamp(48px, 12vw, 72px);
            }
            
            .impact-grid {
              grid-template-columns: 1fr;
              gap: 20px;
            }
            
            .impact-card {
              padding: 30px;
            }
            
            .impact-number {
              font-size: clamp(40px, 10vw, 56px);
            }
            
            .projects-header {
              flex-direction: column;
              gap: 15px;
              align-items: flex-start;
            }
            
            .projects-grid {
              grid-template-columns: 1fr;
              gap: 30px;
              padding: 5px;
            }
            
            .project-card {
              margin-right: 5px;
              margin-bottom: 5px;
            }
            
            .project-card:hover {
              transform: translate(-2px, -2px);
              box-shadow: 10px 10px 0 #000;
            }
            
            .hero-project-card {
              grid-template-columns: 1fr;
              min-height: auto;
              margin-right: 5px;
              box-shadow: 8px 8px 0 #0021F5;
            }
            
            .hero-project-card:hover {
              transform: translate(-2px, -2px);
              box-shadow: 10px 10px 0 #0021F5;
            }
            
            .hero-project-content {
              padding: 30px 20px;
            }
            
            .hero-project-name {
              font-size: 24px;
            }
            
            .hero-project-question {
              font-size: 18px;
            }
            
            .hero-project-image-container {
              height: 250px;
              padding: 15px;
            }
            
            .project-image-container {
              height: 200px;
              padding: 15px;
            }
            
            .project-content {
              padding: 20px;
            }
            
            .project-name {
              font-size: 16px;
            }
            
            .project-question {
              font-size: 18px;
            }
          }
          
          /* Tablet styles */
          @media (min-width: 769px) and (max-width: 1024px) {
            .hero-section {
              padding: 60px 40px;
            }
            
            .impact-grid {
              grid-template-columns: repeat(2, 1fr);
            }
            
            .hero-project-card {
              grid-template-columns: 1fr;
            }
          }
          
          /* Small screens */
          @media (max-width: 480px) {
            .hero-section {
              padding: 30px 15px;
            }
            
            .name-title {
              font-size: 28px;
            }
            
            .design-philosophy {
              font-size: 18px;
              line-height: 1.4;
            }
            
            .bio-pill {
              padding: 10px 15px;
              font-size: 14px;
              box-shadow: 3px 3px 0 #000;
            }
            
            .impact-card {
              padding: 25px 20px;
            }
            
            .projects-title {
              font-size: 20px;
            }
          }
          
          /* Arrow */
          .arrow {
            display: inline-block;
            width: 16px;
            height: 16px;
            border-top: 2px solid currentColor;
            border-right: 2px solid currentColor;
            transform: rotate(45deg);
            transition: transform 0.3s ease;
          }



          /* Hide native cursor only over the name */
.aditya-name:hover { cursor: none; }

/* Cursor container */
.build-cursor{
  position: fixed; top:0; left:0; z-index:9999; pointer-events:none;
  opacity:0; transform:translate(-50%,-50%) scale(.96);
  transition:opacity .18s ease, transform .18s ease;
}

/* Sleek pill */
.build-cursor__badge{
  display:inline-flex; align-items:center; gap:18px;
  padding:20px 28px; border-radius:999px;
  background:linear-gradient(180deg,#111 0%, #1a1a1a 100%);
  border:1px solid rgba(255,255,255,.08);
  box-shadow:0 8px 24px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.08);
  color:#fff; font:800 16px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
  letter-spacing:.05em; text-transform:uppercase; white-space:nowrap;
}

/* Sparkline container (fixed size for crispness) */
.sparkline{
  width:36px; height:14px; display:block;
}

/* Sparkline path + glow */
.sparkline__path{
  fill:none; stroke:#ADFF00; stroke-width:2; stroke-linecap:round; opacity:.95;
  filter: drop-shadow(0 0 2px rgba(173,255,0,.6));
}
.sparkline__glow{
  fill:#ADFF00; opacity:.95; filter: drop-shadow(0 0 4px rgba(173,255,0,.9));
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce){
  .build-cursor{ transition:opacity .18s ease; transform:translate(-50%,-50%); }
}
.build-cursor__badge { overflow: visible; }   /* let SVG glow spill outside */
.sparkline { overflow: visible; display: block; }

          
.project-tag {
  display: inline-block;
  padding: 4px 12px;
  background: #0021F5;
  color: white;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.project-name-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.project-card--ghost {
  visibility: hidden;
  pointer-events: none;
}
/* Tighter variants so the heading doesn't consume a viewport */
.hero-section--compact {
  min-height: auto;
  padding: 140px 80px 12px; /* much smaller */
}

.hero-section--tight {
  min-height: auto;
  padding-top: 20px;       /* reduce gap above cards */
}

/* Optional: when a compact hero is followed by a hero, trim extra top padding */
.hero-section--compact + .hero-section {
  padding-top: 20px;
}


        `}
      </style>


{/* Fun stuff heading (same style as Impact) */}
<div className="hero-section hero-section--compact">
  <div className="impact-header">
    <h2 className="impact-title">Fun stuff</h2>
    <p className="impact-subtitle">Little experiments & side quests</p>
  </div>
</div>



<section className="hero-section hero-section--tight">
        <div className="projects-preview">
          <div className="projects-grid">
            <div
              className="project-card"
              onClick={() => handleProjectClick("/creativecoding")}
            >
              <div className="project-image-container">
                <img
                  src={project5Image}
                  alt="Creative Coding"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                <div className="project-name-row">
                  <h4 className="project-name">CREATIVE CODING</h4>
                </div>{" "}
                <p className="project-question">
                  Fun with p5.js
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Keeps me creatively occupied
                </p>
                <a className="view-case-study">
                  View Case Study <span className="arrow"></span>
                </a>
              </div>
            </div>

            <div
              className="project-card"
              onClick={() => handleProjectClick("/emotionalchat")}
            >
              <div className="project-image-container">
                <img
                  src={project6Image}
                  alt="Experimental emotion-sensitive chat"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                  <h4 className="project-name">EMOTION-SENSITIVE CHAT PLATFORM</h4>
                  
                <p className="project-question">
                  An experimental chat platform that rephrases your sent
                  messages based on your live facial expressions
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Makes textual conversations more aware of your emotions, so
                  they are actually reflective of your current mental state
                </p>
                <a className="view-case-study">
                  View Project <span className="arrow"></span>
                </a>
              </div>
            </div>

            <div
              className="project-card"
              onClick={() => handleProjectClick("/owly")}
              role="link"
              aria-label="Open Owly website in a new tab"
            >
              <div className="project-image-container">
                <img src={owlyImage} alt="Owly" className="project-image" />
              </div>
              <div className="project-content">
                <div className="project-name-row">
                  <h4 className="project-name">OWLY</h4>
                  <span className="project-tag">Vibe-coded</span>
                </div>
                <p className="project-question">
                  Built a Chrome Extension (design + dev + PM) that reorganizes
                  your calendar based on your auto-captured energy levels.
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Piloted with 20 testers (students + pros); 75% reported
                  clearer schedules, less context-switching & more control of
                  their day.
                </p>
                <a className="view-case-study">
                  View Website <span className="arrow"></span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};

export default withAuth(Home);
